import { useNavigate } from "react-router-dom";
import { LoadingCircle } from "../common/loading";
import { openLink } from "../../services/openLink";
import { useAuth } from "../../context/authProvider";
import { initiateGuestAuth } from "../../services/auth";
import { useAuthStore } from "../../stores/useAuthStore";
import { isValidEmail } from "../../utils/emailValidator";
import { fetchAccountData } from "../../services/account";
import { PhoneValidator } from "../../utils/phoneValidator";
import { useStep } from "../../stores/buyerHomeFeaturesStore";
import { useUserProfileStore } from "../../stores/profileStore";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
// import AppleIcon from "../../assets/SocialMedia/appleIcon.svg";
// import GoogleIcon from "../../assets/SocialMedia/googleIcon.svg";
import { useVerificationStore } from "../../stores/verificationStore";
// import FaceBookIcon from "../../assets/SocialMedia/facebookIcon.svg";
import {
	useState,
	useEffect,
	useRef,
	useCallback,
	type ChangeEvent,
} from "react";

const LoginEmailView = () => {
	const [localEmail, setLocalEmail] = useState<string>("");
	const [localPhoneNumber, setLocalPhoneNumber] = useState<string>("");
	const [countdown, setCountdown] = useState(0);
	const { setAuthenticated } = useAuth();
	const { setUserProfile } = useUserProfileStore();
	const { setCurrentStep } = useStep();

	const { setAuthEmail, setAuthPhoneNumber, setPhoneNumberVerification } =
		useAuthStore();
	const [loading, setLoading] = useState(false);
	const { verificationFailed, setVerificationFailed } = useVerificationStore();
	const [agreementChecked, setAgreementChecked] = useState(false);
	const onboardingButtonRef = useRef<HTMLButtonElement>(null);
	const navigate = useNavigate();
	const [emailError, setEmailError] = useState("");
	const [phoneError, setPhoneError] = useState("");

	const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
		setLocalEmail(e.target.value.toLowerCase());
		if (emailError) {
			setEmailError("");
		}
	};

	const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
		setLocalPhoneNumber(formatPhoneNumber(e.target.value));
		if (phoneError) {
			setPhoneError("");
		}
	};

	const validateEmail = (): boolean => {
		if (!localEmail) {
			setEmailError("Email cannot be empty.");
			return false;
		}
		if (!isValidEmail(localEmail)) {
			setEmailError("Please enter a valid email address.");
			return false;
		}
		setEmailError("");
		return true;
	};

	const validatePhoneNumber = (): boolean => {
		if (!localPhoneNumber.trim()) {
			setPhoneError("Phone number cannot be empty.");
			return false;
		}
		if (
			PhoneValidator.isValid(localPhoneNumber, "CA") ||
			PhoneValidator.isValid(localPhoneNumber, "US")
		) {
			setPhoneError("");
			return true;
		}
		setPhoneError("Please enter a valid phone number.");
		return false;
	};

	useEffect(() => {
		if (verificationFailed) {
			setCountdown(10);
			const timer = setInterval(() => {
				setCountdown((c) => {
					if (c <= 1) {
						clearInterval(timer);
						setVerificationFailed(false);
						return 0;
					}
					return c - 1;
				});
			}, 1000);
			return () => clearInterval(timer);
		}
	}, [verificationFailed, setVerificationFailed]);

	const handleKeyDown = useCallback((e: KeyboardEvent) => {
		if (e.key === "Enter" && onboardingButtonRef.current) {
			onboardingButtonRef.current.click();
		}
	}, []);

	useEffect(() => {
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	const handleClick = async () => {
		const emailValid = validateEmail();
		const phoneValid = validatePhoneNumber();

		if (!agreementChecked) {
			return;
		}
		if (!(emailValid && phoneValid)) {
			return;
		}

		setLoading(true);
		try {
			let formattedPhoneNumber = "";
			if (PhoneValidator.isValid(localPhoneNumber, "CA")) {
				formattedPhoneNumber = PhoneValidator.formatE164(
					localPhoneNumber,
					"CA",
				);
			} else if (PhoneValidator.isValid(localPhoneNumber, "US")) {
				formattedPhoneNumber = PhoneValidator.formatE164(
					localPhoneNumber,
					"US",
				);
			}

			const response = await initiateGuestAuth(
				localEmail,
				formattedPhoneNumber,
			);

			if (response.status === "SUCCESS") {
				const accountData = await fetchAccountData();
				setUserProfile(accountData);

				const { firstName, lastName, phoneNumber } = accountData;
				const missingEssentialDetails =
					!(firstName && lastName) || !phoneNumber;

				if (missingEssentialDetails) {
					setAuthenticated(true);
					setCurrentStep(0);
					navigate("/buyer-input-flow/details-input");
					return true;
				}

				const incomeAmount = accountData.finances?.income?.[0]?.amount || 0;
				const savingsAmount = accountData.finances?.savings?.[0]?.amount || 0;
				const monthlyDebtAmount =
					accountData.finances?.monthlyDebt?.[0]?.amount || 0;

				if (incomeAmount === 0) {
					setAuthenticated(true);
					setCurrentStep(1);
					navigate("/buyer-input-flow/annual-income");
					return true;
				}

				if (savingsAmount === 0) {
					setAuthenticated(true);
					setCurrentStep(2);
					navigate("/buyer-input-flow/saving");
					return true;
				}

				if (monthlyDebtAmount === 0) {
					setAuthenticated(true);
					setCurrentStep(3);
					navigate("/buyer-input-flow/monthly-debt");
					return true;
				}

				setAuthenticated(true);
				navigate("/");
			}

			if (response.challengeType === "SMS") {
				setPhoneNumberVerification(true);
			}
			setAuthEmail(localEmail);
			setAuthPhoneNumber(formattedPhoneNumber);
			navigate("/login/verify-view");
		} catch (err) {
			console.error("Auth error:", err);
			setVerificationFailed(true);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex flex-col items-center justify-center h-full space-y-4 pt-8  relative">
			{loading && (
				<div className="flex justify-center items-center">
					<LoadingCircle />
				</div>
			)}
			{!loading && (
				<>
					<div className="w-full space-y-6">
						<div className="flex flex-col w-full space-y-2">
							<div className="font-semibold text-lg">Email Address</div>
							<div className="w-full flex flex-col">
								<input
									type="email"
									placeholder="Enter email address"
									autoComplete="off"
									value={localEmail}
									onChange={handleEmailChange}
									className={`border-2 rounded-full h-[63px] py-4 px-3 bg-z-dark-moss-green/5 placeholder:text-[#7C7C7C] ${emailError ? "border-red-500" : "border-z-dark-moss-green"}`}
								/>
								{emailError && (
									<div className="text-red-500 text-left mt-1">
										{emailError}
									</div>
								)}
							</div>
						</div>
						<div className="flex flex-col w-full space-y-2">
							<div className="font-semibold text-lg">Phone Number</div>
							<div className="w-full flex flex-col">
								<input
									type="tel"
									placeholder="Enter your phone number"
									autoComplete="off"
									value={localPhoneNumber}
									onChange={handlePhoneNumberChange}
									className={`border-2 rounded-full h-[63px] py-4 px-3 bg-z-dark-moss-green/5 placeholder:text-[#7C7C7C] ${phoneError ? "border-red-500" : "border-z-dark-moss-green"}`}
								/>
								{phoneError && (
									<div className="text-red-500 text-left mt-1">
										{phoneError}
									</div>
								)}
							</div>
						</div>
						<div className="flex items-center">
							<input
								id="default-checkbox"
								type="checkbox"
								checked={agreementChecked}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									setAgreementChecked(e.target.checked)
								}
								className="w-[30px] h-[30px] rounded-[20px] text-z-dark-moss-green bg-[#F9F9F9] border-[#A4A4A4]"
							/>
							<label
								htmlFor="default-checkbox"
								className="ms-2 text-sm font-medium h-full text-[#32302F]"
							>
								I agree to the{" "}
								<button
									type="button"
									onClick={() =>
										openLink("https://zown.ca/terms-and-conditions")
									}
									className="underline"
								>
									terms and conditions
								</button>{" "}
								and{" "}
								<button
									onClick={() => openLink("https://zown.ca/privacy-policy")}
									type="button"
									className="underline"
								>
									privacy policy.
								</button>
							</label>
						</div>

						<button
							ref={onboardingButtonRef}
							type="button"
							className="bg-z-dark-moss-green text-white disabled:bg-[#E0E0E0] disabled:text-[#939393] w-full py-4 rounded-full font-bold shadow-md"
							onClick={handleClick}
							disabled={countdown > 0 || !agreementChecked}
						>
							{`Send Code${countdown > 0 ? ` (${countdown})` : ""}`}
						</button>
					</div>
					{/* <div className="flex w-full gap-2 items-center text-center">
            <hr className="h-px my-8 bg-black 0 border-0 w-full" />
            <div className="text-sm font-medium w-full">Or Login with</div>
            <hr className="h-px my-8 bg-black border-0 w-full" />
          </div>
          <div className="w-full flex items-center justify-center h-[60px] gap-3">
            <button
              type="button"
              className="w-full flex justify-center border-[#EBEBEB] border-2 rounded-full items-center h-full"
            >
              <img src={FaceBookIcon} alt="" />
            </button>
            <button
              type="button"
              className="w-full flex justify-center border-[#EBEBEB] border-2 rounded-full items-center h-full"
            >
              <img src={GoogleIcon} alt="" />
            </button>
            <button
              type="button"
              className="w-full flex justify-center border-[#EBEBEB] border-2 rounded-full items-center h-full"
            >
              <img src={AppleIcon} alt="" />
            </button>
          </div> */}
				</>
			)}
		</div>
	);
};

export { LoginEmailView };
